import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ImageOff } from 'lucide-react';

export interface ThumbnailProps {
  /**
   * Image URL. The public catalog DTOs fall back to an empty string instead of
   * null, so empty must be treated exactly like missing - otherwise the row
   * renders the browser's broken-image glyph.
   */
  src?: string;
  /** Describes the pictured record (ad title, store name...). */
  alt: string;
  /** Frame width in px (default 88). The height follows `aspectRatio`. */
  width?: number;
  /** Frame ratio as `W:H` (default `16:9`, the published cover ratio). */
  aspectRatio?: string;
  className?: string;
}

const Frame = styled.div<{ $width: number; $aspect: string }>`
  position: relative;
  flex: 0 0 auto;
  width: ${(p) => p.$width}px;
  aspect-ratio: ${(p) => p.$aspect};
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Placeholder = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.theme.textMuted};
`;

/**
 * Fixed-ratio image thumbnail for lists and tables. Keeps the frame occupied
 * when there is no image, so a record without a cover stays a quiet neutral
 * tile instead of a hole in the layout (or a broken-image icon).
 */
export const Thumbnail: React.FC<ThumbnailProps> = ({
  src,
  alt,
  width = 88,
  aspectRatio = '16:9',
  className,
}) => {
  const [failed, setFailed] = useState(false);

  // A row can be re-rendered with a different record (pagination, filters).
  useEffect(() => setFailed(false), [src]);

  const aspect = aspectRatio.includes(':') ? aspectRatio.replace(':', ' / ') : aspectRatio;
  const hasImage = !!src && src.trim().length > 0 && !failed;

  return (
    <Frame $width={width} $aspect={aspect} className={className}>
      {hasImage ? (
        <Image src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <Placeholder title="Sem imagem">
          <ImageOff size={Math.max(14, Math.round(width / 5))} />
        </Placeholder>
      )}
    </Frame>
  );
};

export default Thumbnail;
